import type { KeyboardEvent, ReactNode } from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Button from "ui/buttons/Button";
import cx from "ui/cx";
import Checkbox from "ui/forms/Checkbox";
import type { InputRef } from "ui/forms/Input";
import Input from "ui/forms/Input";
import IconClose from "ui/icons/IconClose";
import IconMiniChevronDown from "ui/icons/IconMiniChevronDown";
import IconSearch from "ui/icons/IconSearch";
import { Popover } from "ui/popovers/Popover/Popover";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import styles from "./MultiSelect.module.scss";

interface MultiSelectOptionBase {
  disabled?: boolean;
  tooltip?: ReactNode;
}

export interface MultiSelectOption extends MultiSelectOptionBase {
  label: string;
  value: string;
  /** Optional inline element rendered immediately after the option label (e.g., notice/warning badge) */
  labelInlineNotice?: ReactNode;
  options?: undefined;
}

export interface MultiSelectOptionGroup extends MultiSelectOptionBase {
  label?: ReactNode;
  options: MultiSelectOption[];
  value?: undefined;
}

type MultiSelectOptions = MultiSelectOption[] | MultiSelectOptionGroup[];

interface MultiSelectProps {
  /** An array of options or option groups */
  options: MultiSelectOptions;
  /** The currently selected values */
  value: string[];
  /** The callback when the selected values change */
  onChange: (value: string[]) => void;
  /** The placeholder text for the select input */
  placeholder?: string;
  /** Whether to force the placeholder to be shown even if there are selected values */
  forcePlaceholder?: boolean;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Whether to show the filter input */
  filterable?: boolean;
  /** Whether to show select all/deselect all options */
  showBulkActions?: boolean;
  /** Whether to show a badge with the number of selected values */
  showCountBadge?: boolean;
  /** The maximum height of the option list in pixels (excluding the filter and bulk actions) */
  maxHeight?: number;
  className?: string;
  dataTestId?: string;
  /** Optional notification element shown inside the trigger (left side), for warnings/tips/status */
  icon?: ReactNode;
  /** Borderless trigger until hover/open, transparent background (shows parent row color). */
  transparent?: boolean;
}

/**
 * Individual option row, memoized so only the options whose
 * checked/focused state actually changed re-render, not all siblings.
 *
 * The constant re-rendering of the options list is a performance issue and memoization here can help.
 */
const MemoOption = memo(function MemoOption({
  option,
  index,
  isSelected,
  isFocused,
  onChangeOption,
  onFocus,
}: {
  option: MultiSelectOption;
  index: number;
  isSelected: boolean;
  isFocused: boolean;
  onChangeOption: (optionValue: string, checked: boolean) => void;
  onFocus: (index: number) => void;
}) {
  const labelNode = (
    <span>
      {option.label}
      {option.labelInlineNotice}
    </span>
  );

  return (
    <Checkbox
      role="option"
      data-testid={`option-${option.value}-${index}`}
      data-option-index={index}
      className={cx(styles.option, {
        [styles.selected]: isSelected,
        [styles.focused]: isFocused,
        [styles.disabled]: option.disabled,
      })}
      tooltip={option.tooltip}
      label={labelNode}
      checked={isSelected}
      disabled={option.disabled}
      onChangeBoolean={(checked) => {
        if (!option.disabled) {
          onChangeOption(option.value, checked);
        }
      }}
      onFocus={() => {
        if (!option.disabled) {
          onFocus(index);
        }
      }}
      onMouseEnter={() => {
        if (!option.disabled) {
          onFocus(index);
        }
      }}
    />
  );
});

export default function MultiSelect({
  options,
  value = [],
  onChange,
  placeholder = "Select options",
  forcePlaceholder = false,
  disabled = false,
  filterable = false,
  showBulkActions = false,
  showCountBadge = false,
  maxHeight = 350,
  className,
  dataTestId,
  icon,
  transparent = false,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filterQuery, setFilterQuery] = useState("");
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  const filterInputRef = useRef<InputRef>(null);
  const optionsListRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Stable refs so callbacks don't invalidate on every value/onChange change.
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  /** A flattened array of all options */
  const allOptions = useMemo(() => {
    return options.flatMap((option) => (option.options ? option.options : [option]));
  }, [options]);

  /** Disable bulk actions & filtering if only 1 option */
  if (allOptions.length < 2) {
    filterable = false;
    showBulkActions = false;
  }

  /** A filtered array of options that maintains grouping */
  const filteredOptionsByGroup = useMemo(() => {
    if (!filterQuery) {
      return options;
    }
    const lowerQuery = filterQuery.toLowerCase();
    return options
      .map((option) => {
        if (option.options) {
          const filteredOptionGroup: MultiSelectOptionGroup = {
            ...option,
            options: option.options.filter((opt) => opt.label.toLowerCase().includes(lowerQuery)),
          };

          return filteredOptionGroup;
        }

        return option;
      })
      .filter((option) =>
        option.options ? option.options.length > 0 : option.label.toLowerCase().includes(lowerQuery),
      );
  }, [options, filterQuery]);

  /** A filtered array of options */
  const filteredOptions = useMemo(() => {
    if (!filterQuery) {
      return allOptions;
    }

    return allOptions.filter((option) => option.label.toLowerCase().includes(filterQuery.toLowerCase()));
  }, [allOptions, filterQuery]);

  const selectedLabels = useMemo(() => {
    return allOptions.filter((option) => value.includes(option.value)).map((option) => option.label);
  }, [allOptions, value]);

  /** Flag if all options are selected, only computed when the dropdown is open and bulk actions are shown */
  const allSelected = useMemo(() => {
    if (!isOpen || !showBulkActions) {
      return false;
    }
    const selectableOptions = allOptions.filter((option) => !option.disabled);
    return selectableOptions.length > 0 && selectableOptions.every((option) => value.includes(option.value));
  }, [isOpen, showBulkActions, allOptions, value]);

  /** Flag if some options are selected, only computed when the dropdown is open and bulk actions are shown */
  const someSelected = useMemo(() => {
    if (!isOpen || !showBulkActions) {
      return false;
    }
    const selectableOptions = allOptions.filter((option) => !option.disabled);
    return selectableOptions.some((option) => value.includes(option.value)) && !allSelected;
  }, [isOpen, showBulkActions, allOptions, allSelected, value]);

  const handleBulkAction = useCallback(
    (checked: boolean) => {
      const selectableOptions = allOptions.filter((option) => !option.disabled);
      const newValues = checked ? selectableOptions.map((option) => option.value) : [];
      onChangeRef.current(newValues);
    },
    [allOptions],
  );

  // Stable, reads value from ref so it doesn't invalidate when value
  // changes. This keeps MemoOption instances stable across re-renders.
  const handleOptionChange = useCallback((optionValue: string, checked: boolean) => {
    const currentValue = valueRef.current;
    const newValues = checked ? [...currentValue, optionValue] : currentValue.filter((v) => v !== optionValue);
    onChangeRef.current(newValues);
  }, []);

  // Reset focus when dropdown opens/closes
  useEffect(() => {
    if (isOpen) {
      setFocusedIndex(-1);
      setFilterQuery("");
    }
  }, [isOpen]);

  const handleOpenAutoFocus = useCallback((e: Event) => {
    e.preventDefault();
    if (filterInputRef.current) {
      filterInputRef.current.focus();
    } else if (contentRef.current) {
      contentRef.current.focus();
    }
  }, []);

  const focusOption = useCallback((index: number) => {
    const optionsList = optionsListRef.current;
    if (!optionsList) {
      return;
    }
    const focusedOption = optionsList.querySelector<HTMLElement>(`[data-option-index="${index}"]`);
    if (focusedOption) {
      focusedOption.focus({ preventScroll: true });
      focusedOption.scrollIntoView({ block: "nearest" });
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const isFilterInput = e.target instanceof HTMLInputElement;
      const optionCount = filteredOptions.length;
      if (optionCount === 0) {
        return;
      }

      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          const next = focusedIndex < optionCount - 1 ? focusedIndex + 1 : focusedIndex;
          setFocusedIndex(next);
          focusOption(next);
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          const prev = focusedIndex > 0 ? focusedIndex - 1 : 0;
          setFocusedIndex(prev);
          focusOption(prev);
          break;
        }
        case "Enter":
        case " ": {
          if (isFilterInput) {
            break;
          }
          if (focusedIndex >= 0 && focusedIndex < optionCount) {
            e.preventDefault();
            const option = filteredOptions[focusedIndex];
            if (!option.disabled) {
              const isSelected = valueRef.current.includes(option.value);
              handleOptionChange(option.value, !isSelected);
            }
          }
          break;
        }
        case "Home": {
          if (isFilterInput) {
            break;
          }
          e.preventDefault();
          setFocusedIndex(0);
          focusOption(0);
          break;
        }
        case "End": {
          if (isFilterInput) {
            break;
          }
          e.preventDefault();
          const last = optionCount - 1;
          setFocusedIndex(last);
          focusOption(last);
          break;
        }
      }
    },
    [filteredOptions, focusedIndex, handleOptionChange, focusOption],
  );

  const renderFlatOption = (option: MultiSelectOption, index: number): ReactNode => {
    return (
      <MemoOption
        key={`${option.value}-${index}`}
        option={option}
        index={index}
        isSelected={value.includes(option.value)}
        isFocused={focusedIndex === index}
        onChangeOption={handleOptionChange}
        onFocus={setFocusedIndex}
      />
    );
  };

  const renderOptionGroup = (group: MultiSelectOptionGroup, groupIndex: number): ReactNode => {
    const label = group.label
      ? wrapWithTooltip(<div className={styles.groupHeader}>{group.label}</div>, group.tooltip)
      : null;
    return (
      <div key={typeof group.label === "string" ? group.label : groupIndex} className={styles.optionGroup}>
        {label}
        {group.options.map((option, optionIndex) => {
          let absoluteIndex = optionIndex;
          for (let i = 0; i < groupIndex; i++) {
            const g = filteredOptionsByGroup[i];
            absoluteIndex += g.options?.length ?? 1;
          }
          return renderFlatOption(option, absoluteIndex);
        })}
        <div className={styles.groupSeparator} />
      </div>
    );
  };

  const showPlaceholder = selectedLabels.length === 0 || forcePlaceholder;

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Trigger asChild>
        <Button
          btnStyle="noStyle"
          role="combobox"
          dataTestId={dataTestId ?? "multiselect-trigger"}
          disabled={disabled}
          className={cx(
            styles.inputWrapper,
            {
              [styles.open]: isOpen,
              [styles.disabled]: disabled,
              [styles.placeholder]: showPlaceholder,
              [styles.transparent]: transparent,
            },
            className,
          )}
        >
          {icon}
          {showCountBadge && selectedLabels.length > 0 && (
            <span data-testid="count-badge" className={styles.countBadge}>
              {selectedLabels.length}
            </span>
          )}
          <span className={styles.inputText}>{!showPlaceholder ? selectedLabels.join(", ") : placeholder}</span>
          <div className={styles.icons}>
            {!showPlaceholder && (
              <div
                data-testid="clear-button"
                className={styles.clearButton}
                onClick={(e) => {
                  e.stopPropagation();
                  onChangeRef.current([]);
                }}
              >
                <IconClose />
              </div>
            )}
            <div
              className={cx(styles.chevron, {
                [styles.open]: isOpen,
              })}
            >
              <IconMiniChevronDown />
            </div>
          </div>
        </Button>
      </Popover.Trigger>
      <Popover.Content className={styles.dropdown} side="bottom" align="start" onOpenAutoFocus={handleOpenAutoFocus}>
        <div ref={contentRef} tabIndex={-1} onKeyDown={handleKeyDown}>
          {filterable && (
            <div className={styles.filter}>
              <IconSearch />
              <Input
                ref={filterInputRef}
                data-testid="filter-input"
                wrapperClassName={styles.filterInputWrapper}
                className={styles.filterInput}
                placeholder="Filter..."
                inputSize="medium"
                transparent
                value={filterQuery}
                onChange={(e) => setFilterQuery(e.target.value)}
              />
            </div>
          )}

          {showBulkActions && (
            <div className={styles.bulkActions}>
              <Checkbox
                data-testid="bulk-action"
                className={styles.bulkAction}
                label={allSelected ? "Deselect all" : "Select all"}
                checked={someSelected ? "indeterminate" : allSelected}
                onChangeBoolean={(checked) => {
                  handleBulkAction(checked);
                }}
              />
            </div>
          )}

          <div
            ref={optionsListRef}
            role="listbox"
            data-testid="options-list"
            className={styles.optionsList}
            style={{ maxHeight: `${maxHeight}px` }}
          >
            {filteredOptionsByGroup.map((option, index) =>
              option.options ? renderOptionGroup(option, index) : renderFlatOption(option, index),
            )}
          </div>

          {filteredOptions.length === 0 && (
            <div data-testid="no-options-found" className={styles.noResults}>
              No results found
            </div>
          )}
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}
